import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const styles = {
  container: { padding: '20px' },
  filtersContainer: {
    background: '#f8f9fa',
    padding: '15px',
    borderRadius: '8px',
    marginBottom: '16px',
    border: '1px solid #e9ecef',
  },
  filterGroup: { display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end' },
  filterItem: { display: 'flex', flexDirection: 'column', gap: '4px' },
  filterLabel: { fontSize: '0.8rem', fontWeight: 600, color: '#495057' },
  input: { padding: '8px 10px', borderRadius: '6px', border: '1px solid #dee2e6', fontSize: '0.95rem' },
  select: { padding: '8px 10px', borderRadius: '6px', border: '1px solid #dee2e6', minWidth: '160px' },
  button: {
    padding: '8px 16px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 500,
    background: '#0d6efd',
    color: '#fff',
  },
  tableContainer: {
    marginTop: '8px',
    overflowX: 'auto',
    border: '1px solid #dee2e6',
    borderRadius: '8px',
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' },
  th: {
    borderBottom: '2px solid #dee2e6',
    padding: '10px',
    background: '#f8f9fa',
    textAlign: 'center',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  thSortable: {
    cursor: 'pointer',
    userSelect: 'none',
    color: '#0d6efd',
  },
  td: { borderBottom: '1px solid #eee', padding: '8px', textAlign: 'center', whiteSpace: 'nowrap' },
  hint: { fontSize: '0.85rem', color: '#6c757d', marginTop: '8px' },
  infoBoxGeneral: {
    marginBottom: 12,
    padding: '14px 16px',
    background: '#e8f4fd',
    border: '1px solid #90caf9',
    borderRadius: 8,
    fontSize: '0.92rem',
    lineHeight: 1.65,
    color: '#0d47a1',
  },
  infoBoxWarning: {
    marginBottom: 16,
    padding: '14px 16px',
    background: '#fff8e1',
    border: '1px solid #ffb300',
    borderRadius: 8,
    fontSize: '0.92rem',
    lineHeight: 1.65,
    color: '#5d4037',
  },
};

const STATUS_OPTIONS = [
  { value: 'all', label: '전체' },
  { value: '일치', label: '일치' },
  { value: '일치(미출력)', label: '일치(미출력)' },
  { value: '일치(출력완료)', label: '일치(출력완료)' },
  { value: '급여자료 출력', label: '급여자료 출력' },
  { value: '불일치 포함', label: '불일치 포함' },
  { value: '참여자 없음 포함', label: '참여자 없음 포함' },
  { value: '불일치·참여자없음', label: '불일치·참여자없음' },
  { value: '데이터 없음', label: '데이터 없음' },
];

const formatVerifiedAt = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return '-';
  }
};

const LeaveVerificationHistoryPage = () => {
  const now = new Date();
  const [searchYear, setSearchYear] = useState(now.getFullYear());
  const [searchMonth, setSearchMonth] = useState(now.getMonth() + 1);
  const [searchProjectId, setSearchProjectId] = useState('all');
  const [searchStatus, setSearchStatus] = useState('all');
  const [projects, setProjects] = useState([]);

  const [sortBy, setSortBy] = useState('verifiedAt');
  const [sortOrder, setSortOrder] = useState('desc');

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchProjects = useCallback(async () => {
    if (!searchYear) return;
    try {
      const res = await api.get('/projects', { params: { year: searchYear } });
      setProjects(res.data || []);
    } catch {
      setProjects([]);
    }
  }, [searchYear]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const fetchLogs = useCallback(
    async (override) => {
      const sb = override?.sortBy ?? sortBy;
      const so = override?.sortOrder ?? sortOrder;
      setLoading(true);
      setError('');
      try {
        const res = await api.get('/leave/verify/logs', {
          params: {
            year: searchYear,
            month: searchMonth === 'all' ? 'all' : searchMonth,
            projectId: searchProjectId === 'all' ? 'all' : searchProjectId,
            verificationStatus: searchStatus === 'all' ? 'all' : searchStatus,
            sortBy: sb,
            sortOrder: so,
          },
        });
        setLogs(res.data || []);
      } catch (err) {
        setLogs([]);
        setError(err.response?.data?.message || '검증 이력을 불러오지 못했습니다.');
      } finally {
        setLoading(false);
      }
    },
    [searchYear, searchMonth, searchProjectId, searchStatus, sortBy, sortOrder]
  );

  useEffect(() => {
    fetchLogs({ sortBy: 'verifiedAt', sortOrder: 'desc' });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 최초 1회만 자동 조회
  }, []);

  const handleSearch = () => {
    setSortBy('verifiedAt');
    setSortOrder('desc');
    fetchLogs({ sortBy: 'verifiedAt', sortOrder: 'desc' });
  };

  const handleSortClick = (column) => {
    const nextOrder =
      sortBy === column ? (sortOrder === 'asc' ? 'desc' : 'asc') : column === 'verifiedAt' ? 'desc' : 'asc';
    setSortBy(column);
    setSortOrder(nextOrder);
    fetchLogs({ sortBy: column, sortOrder: nextOrder });
  };

  const sortArrow = (column) => {
    if (sortBy !== column) return null;
    return sortOrder === 'asc' ? ' ↑' : ' ↓';
  };

  const thSortable = (column, label) => (
    <th
      style={{ ...styles.th, ...styles.thSortable }}
      onClick={() => handleSortClick(column)}
      title="클릭하여 정렬"
    >
      {label}
      {sortArrow(column)}
    </th>
  );

  const displayStatus = (row) => row.displayVerificationStatus ?? row.verificationStatus ?? '-';

  return (
    <div style={styles.container}>
      <h2>검증 이력 조회</h2>

      <div style={styles.infoBoxGeneral}>
        연차·급여 <strong>검증 실행</strong>(이력에 저장되는 경우)마다 기록되며, <strong>검증·출력일시</strong>는 해당 실행
        시각입니다. <strong>급여자료 출력</strong> 화면에서 <strong>개별 사업단</strong>을 선택한 뒤 엑셀을 내려받은
        이력은 &quot;급여자료 출력&quot;으로 함께 표시됩니다(전체 사업 출력은 이력에 포함되지 않습니다).{' '}
        <strong>검색</strong> 시 정렬은 최근 일시순으로 초기화됩니다.
      </div>

      <div style={styles.infoBoxWarning}>
        <strong>일치(미출력)</strong>으로 표시된 건은, 급여 자료 입력 이후 담당자가{' '}
        <strong>급여자료 출력</strong>에서 해당 <strong>사업단 기준</strong>으로 엑셀을 내려받지 않은 경우가 많아,
        실제 결재·제출 서류에 반영되지 않았을 가능성이 있습니다. 필요 시 내용을 다시 한 번 확인해 주세요.
      </div>

      <div style={styles.filtersContainer}>
        <div style={styles.filterGroup}>
          <div style={styles.filterItem}>
            <span style={styles.filterLabel}>연도</span>
            <input
              type="number"
              style={styles.input}
              value={searchYear}
              min={2020}
              onChange={(e) => setSearchYear(Number(e.target.value))}
            />
          </div>
          <div style={styles.filterItem}>
            <span style={styles.filterLabel}>월</span>
            <select style={styles.select} value={searchMonth} onChange={(e) => setSearchMonth(e.target.value === 'all' ? 'all' : Number(e.target.value))}>
              <option value="all">전체</option>
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>
                  {m}월
                </option>
              ))}
            </select>
          </div>
          <div style={styles.filterItem}>
            <span style={styles.filterLabel}>사업단</span>
            <select style={styles.select} value={searchProjectId} onChange={(e) => setSearchProjectId(e.target.value)}>
              <option value="all">전체</option>
              {(projects || []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.projectName}
                </option>
              ))}
            </select>
          </div>
          <div style={styles.filterItem}>
            <span style={styles.filterLabel}>검증 및 출력상태</span>
            <select style={styles.select} value={searchStatus} onChange={(e) => setSearchStatus(e.target.value)}>
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div style={styles.filterItem}>
            <span style={styles.filterLabel}>&nbsp;</span>
            <button type="button" style={styles.button} onClick={handleSearch} disabled={loading}>
              {loading ? '조회 중...' : '검색'}
            </button>
          </div>
        </div>
      </div>

      {error && <p style={{ color: 'red', marginBottom: '8px' }}>{error}</p>}

      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>연도</th>
              <th style={styles.th}>월</th>
              {thSortable('projectName', '사업단')}
              <th style={styles.th}>검증자</th>
              {thSortable('verifiedAt', '검증 또는 출력일시')}
              {thSortable('verificationStatus', '검증 및 출력상태')}
            </tr>
          </thead>
          <tbody>
            {logs.length === 0 ? (
              <tr>
                <td colSpan={6} style={styles.td}>
                  {loading ? '불러오는 중...' : '조회된 이력이 없습니다.'}
                </td>
              </tr>
            ) : (
              logs.map((row) => {
                const statusText = displayStatus(row);
                const isMismatchOutput = statusText === '일치(미출력)';
                return (
                  <tr key={String(row.id)}>
                    <td style={styles.td}>{row.year}</td>
                    <td style={styles.td}>{row.month}</td>
                    <td style={styles.td}>{row.projectNameSnapshot || '-'}</td>
                    <td style={styles.td}>{row.Verifier?.name || row.Verifier?.userId || '-'}</td>
                    <td style={styles.td}>{formatVerifiedAt(row.createdAt)}</td>
                    <td
                      style={{
                        ...styles.td,
                        ...(isMismatchOutput ? { color: '#c62828', fontWeight: 600 } : {}),
                      }}
                    >
                      {statusText}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      <p style={styles.hint}>
        최대 500건까지 표시됩니다. &quot;전체&quot; 선택 시 검증 이력과 급여자료 출력 이력이 시간순으로 합쳐집니다.
        사업단·검증 또는 출력일시·검증 및 출력상태 열 머리글을 누르면 오름차순·내림차순이 전환됩니다.
      </p>
    </div>
  );
};

export default LeaveVerificationHistoryPage;
